import { ChangeEvent, useEffect, useState } from "react";
import { toast } from "react-toastify";
import AddIcon from "@mui/icons-material/Add";
import { SIMPLMenuHeader } from "~/components/commons/header/SIMPLMenuHeader";
import { SIMPLTable } from "~/components/commons/table/SimplTable";
import { Layout } from "~/components/commons/layout/Layout";
import { SIMPLTitle } from "~/components/commons/title/SIMPLTitle";
import { SIMPLButtonCustomized } from "~/components/commons/button/SIMPLButtonCustumized";
import { SIMPLAlert } from "~/components/commons/dialog/SimplAlert";
import { LoadingWrapper } from "~/components/commons/spiner/SimpleLoaderWrapper";
import { LABELS } from "~/resources/Labels";
import { MESSAGES } from "~/resources/Message";
import { FormCloudProvisionerTemplateResponse, PropRow, SortTable } from "~/models/Commons ";
import { CloudProvisionerTemplateHeader } from "~/models/cloudprovisionertemplate/CloudProvisionerTemplateHeader";
import { useGetCloudProvisionerTemplatesQuery, useDeleteCloudProvisionerTemplateMutation } from "~/services/cloudProvisioner";
import { useAppDispatch } from "~/hooks/appSelector";
import { DELETE_CLOUDPROVISIONERTEMPLATE_BYID } from "~/store/feature/CloudProvisionerTemplate";
import { AddCloudProvisionerTemplate } from "./AddCloudProvisionerTemplate";
import { EditCloudProvisionerTemplate } from "./EditCloudProvisionerTemplate";
import { DetailsViewCloudProvisionerTemplate } from "./DetailsViewCloudProvisionerTemplate";
import { buildLogoutUrl } from "~/services/keycloak";
import useSessionTimeout from "~/services/session/SessionTimeOut";

export const CloudProvisionerTemplatePage = () => {
  const dispatch = useAppDispatch();
  const [deleteTemplate] = useDeleteCloudProvisionerTemplateMutation();

  const [page, setPage] = useState<number>(0);
  const [size, setSize] = useState<number>(10);
  const [total, setTotal] = useState<number>(0);
  const [totalElements, setTotalElements] = useState<number>(0);
  const [showPagination, setShowPagination] = useState(true);
  const [rows, setRows] = useState<PropRow[]>([]);
  const [sortConfig, setSortConfig] = useState<SortTable>({ key: "", direction: LABELS.ASC });
  const [showFilter, setShowFilter] = useState(false);

  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [openDetailView, setOpenDetailView] = useState(false);
  const [openEditView, setOpenEditView] = useState(false);
  const [deleteModal, setDeleteModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState<FormCloudProvisionerTemplateResponse | null>(null);

  const INACTIVITY_LIMIT = Number(import.meta.env.VITE_INACTIVITY_LIMIT);
  const FORCED_LOGOUT_LIMIT_EXPIRED_TOKEN = Number(import.meta.env.VITE_FORCED_LOGOUT_LIMIT_EXPIRED_TOKEN);

  useSessionTimeout({
    inactivityLimit: INACTIVITY_LIMIT,
    forcedLogoutLimit: FORCED_LOGOUT_LIMIT_EXPIRED_TOKEN,
    storage: sessionStorage,
  });

  const { data, refetch, isLoading, isFetching, isError } = useGetCloudProvisionerTemplatesQuery({ page, size });

  useEffect(() => {
    const payload = data?.payload as any;
    if (!payload) {
      setShowPagination(false);
      return;
    }

    const content: FormCloudProvisionerTemplateResponse[] = payload.content ?? [];
    setTotalElements(payload?.page?.totalElements ?? 0);
    setTotal(payload?.page?.totalPages ?? 0);

    const tableRows: PropRow[] = content.map((item) => ({
      row: {
        ...item,
        provider: item.isAzure ? "Azure" : item.isAws ? "AWS" : "—",
        locationOrRegion: item.isAzure
          ? (item.azureLocation || "—")
          : item.isAws
            ? (item.awsRegion || "—")
            : "—",
        sizesOrTypes: item.isAzure
          ? (item.azureVmSizes?.join(", ") || "—")
          : item.isAws
            ? (item.awsInstanceTypes?.join(", ") || "—")
            : "—",
      },
    }));
    setRows(tableRows);
    setShowPagination(content.length > 0);
  }, [data]);

  useEffect(() => {
    setPage(0);
  }, [size]);

  const handleSort = (key: string) => {
    const direction =
      sortConfig.key === key && sortConfig.direction === LABELS.ASC ? LABELS.Desc : LABELS.ASC;
    setSortConfig({ key, direction });
  };

  const handlePageChange = (newPage: number) => setPage(newPage);
  const handleSizeChange = (e: ChangeEvent<HTMLSelectElement>) => setSize(Number(e.target.value));
  const showFilterHandler = () => setShowFilter((prev) => !prev);
  const handleResetAll = () => { setPage(0); setShowFilter(false); };

  const handleOpenDetail = (item: any) => {
    if (item?.id) {
      const content: FormCloudProvisionerTemplateResponse[] = (data?.payload as any)?.content ?? [];
      const found = content.find((t) => t.id === item.id);
      if (found) { setSelectedItem(found); setOpenDetailView(true); }
    }
  };

  const handleEdit = (item: any) => {
    if (item?.id) {
      const content: FormCloudProvisionerTemplateResponse[] = (data?.payload as any)?.content ?? [];
      const found = content.find((t) => t.id === item.id);
      if (found) { setSelectedItem(found); setOpenEditView(true); }
    }
  };

  const handleConfirmDelete = (item: any) => {
    if (item?.id) {
      const content: FormCloudProvisionerTemplateResponse[] = (data?.payload as any)?.content ?? [];
      const found = content.find((t) => t.id === item.id) ?? item;
      setSelectedItem(found);
      setDeleteModal(true);
    }
  };

  const handleDeleteItem = async () => {
    if (!selectedItem?.id) return;
    try {
      setDeleteModal(false);
      toast.loading("Deleting template...");
      await deleteTemplate(selectedItem.id).unwrap();
      dispatch(DELETE_CLOUDPROVISIONERTEMPLATE_BYID(selectedItem.id));
      toast.dismiss();
      toast.success(MESSAGES.MessageCloudProvisionerTemplateRemoved);
    } catch (error) {
      toast.dismiss();
      toast.error(MESSAGES.ErrorDelectingScript);
    } finally {
      refetch();
    }
  };

  const renderDeleteAlert = () =>
    deleteModal && selectedItem && (
      <SIMPLAlert
        onCancel={() => setDeleteModal(false)}
        message={MESSAGES.MessageDialogDelete(selectedItem.fileName)}
        title="Delete Cloud Provisioner Template"
        actions={
          <>
            <div className="grow-0 basis-32 mr-5">
              <SIMPLButtonCustomized label="Cancel" secondary buttonHandlerAction={() => setDeleteModal(false)} />
            </div>
            <div className="grow-0 basis-32">
              <SIMPLButtonCustomized label="Delete Item" danger buttonHandlerAction={handleDeleteItem} />
            </div>
          </>
        }
      />
    );

  const renderAddModal = () =>
    isAddModalOpen && (
      <AddCloudProvisionerTemplate
        onCloseModal={() => setIsAddModalOpen(false)}
        title={LABELS.AddCloudProvisionerTemplate}
        refetch={refetch}
      />
    );

  const renderEditModal = () =>
    openEditView && selectedItem && (
      <EditCloudProvisionerTemplate
        onCloseModal={() => setOpenEditView(false)}
        editedItem={selectedItem}
        title="Update Cloud Provisioner Template"
        refetch={refetch}
      />
    );

  const renderDetailView = () =>
    openDetailView && selectedItem && (
      <DetailsViewCloudProvisionerTemplate item={selectedItem} onClose={() => setOpenDetailView(false)} />
    );

  return (
    <SIMPLMenuHeader>
      <Layout>
        <SIMPLTitle title={LABELS.CloudProvisionerTemplateManagement} />
        {renderDetailView()}
        {renderEditModal()}
        {renderDeleteAlert()}
        {renderAddModal()}
        <LoadingWrapper loadingTitle="Loading templates..." isLoading={isLoading || isFetching} isError={isError}>
          <div className="flex flex-col w-full">
            <div className="mt-5">
              <div className="container mx-auto mt-10">
                <SIMPLTable
                  clear={handleResetAll}
                  button={
                    <SIMPLButtonCustomized
                      label="Add Template"
                      buttonHandlerAction={() => setIsAddModalOpen(true)}
                      firstIcon={<AddIcon fontSize="small" />}
                      secondIcon={<AddIcon fontSize="small" />}
                      secondary
                    />
                  }
                  page={page}
                  total={total}
                  totalElements={totalElements}
                  showPagination={showPagination}
                  active={true}
                  showHandler={showFilterHandler}
                  filterActive={showFilter}
                  tableHeaders={CloudProvisionerTemplateHeader}
                  rows={rows}
                  onChangePage={handlePageChange}
                  onChange={handleSizeChange}
                  handleOpenDetail={handleOpenDetail}
                  handleOpenEdit={handleEdit}
                  handleRowClick={handleOpenDetail}
                  handleDelete={handleConfirmDelete}
                  sortConfig={sortConfig}
                  handleSort={handleSort}
                  chipLabels={[]}
                  showButtonDetails
                  showEdit
                  showButtonDelete
                />
              </div>
            </div>
          </div>
        </LoadingWrapper>
      </Layout>
    </SIMPLMenuHeader>
  );
};
